import { useState } from "react";

import { CodeInput } from "../components/CodeInput";
import { useRequestCode, useVerifyCode } from "../api/hooks";
import { ApiError } from "../api/client";

/**
 * Anmeldung in zwei Schritten: Adresse, dann Code aus der E-Mail.
 *
 * Beide Schritte tragen dieselbe Beschriftung — „Anmelden". Aus Sicht des
 * Lesers ist das eine Handlung, die zwischendurch auf eine Mail wartet;
 * „Code anfordern" hätte daraus zwei gemacht.
 *
 * Erklärender Text ist absichtlich fast keiner da. Wer hier steht, will sich
 * anmelden und nicht lesen; was schiefgehen kann, sagt die Fehlermeldung
 * dann genau und im richtigen Moment.
 */
export function Login() {
  const requestCode = useRequestCode();
  const verifyCode = useVerifyCode();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setError("");
    setCode("");
    setCodeSent(false);
  };

  const fail = (err: unknown, fallback: string) =>
    setError(err instanceof ApiError ? err.message : fallback);

  const sendCode = () => {
    setError("");
    requestCode.mutate(
      { email },
      {
        onSuccess: () => setCodeSent(true),
        onError: (err) => fail(err, "Anfrage fehlgeschlagen."),
      },
    );
  };

  // Der Wert kommt als Argument, wenn das Feld selbst auslöst — der Zustand
  // ist zu diesem Zeitpunkt noch der von vor der letzten Ziffer.
  const submitCode = (value: string = code) => {
    setError("");
    verifyCode.mutate(
      { email, code: value },
      { onError: (err) => fail(err, "Der Code stimmt nicht.") },
    );
  };

  const busy = requestCode.isPending || verifyCode.isPending;

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <img src="/logo.png" alt="Autoflug" className="auth-logo" />

        {!codeSent ? (
          <>
            <label className="auth-label" htmlFor="login-email">
              E-Mail-Adresse
            </label>
            <input
              id="login-email"
              className="auth-input"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="name@domain.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && email) sendCode();
              }}
            />
            {error && <p className="auth-error">{error}</p>}
            <button className="btn btn-block" onClick={sendCode} disabled={busy || !email}>
              {requestCode.isPending ? "Einen Moment …" : "Anmelden"}
            </button>
          </>
        ) : (
          <>
            <label className="auth-label">Code aus der E-Mail</label>
            <p className="auth-sub">{email}</p>
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={(full) => submitCode(full)}
              disabled={verifyCode.isPending}
            />
            {error && <p className="auth-error">{error}</p>}
            <button
              className="btn btn-block"
              onClick={() => submitCode()}
              disabled={busy || code.length !== 6}
            >
              {verifyCode.isPending ? "Einen Moment …" : "Anmelden"}
            </button>
            <button type="button" className="auth-back" onClick={reset}>
              Andere Adresse
            </button>
          </>
        )}
      </div>
    </div>
  );
}
